import { useStickyFacets } from '@common/hooks';
import { NavigationTabsOption } from '@common/types/link';
import { Clip, useClips } from '@features/Clip';
import { useClipColumns } from '@features/Clip/hooks/useClipColumns';
import {
    FacetingContextValue,
    FacetingPanel,
    FacetingProvider,
    SearchResult,
    useCustomFieldDescriptions,
    useDynamicFacets,
    usePinnedFacets,
} from '@features/facetingEngine';
import { TableCard } from '@features/tableEngine/components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CLIP_FACET_CONFIG } from '../constants';
import { NORMALIZATION_STRATEGIES } from '@features/facetingEngine/utils/facetNormalizers';

interface ClipSearchProps {
    tabOptions: NavigationTabsOption<string>[];
}

const PAGE_SIZES = [...Array(5)].map((_, i) => (i + 1) * 25);

export const ClipSearchView = ({ tabOptions }: ClipSearchProps) => {
    const { t } = useTranslation(['clipTable', 'clipDetails']);
    const { data: cfdData } = useCustomFieldDescriptions();

    const config = useDynamicFacets<Clip>(
        cfdData.cfdArray,
        CLIP_FACET_CONFIG,
        // (key) => t(key, { ns: 'facetingPanel' }),
    );

    const {
        pinnedKeys: facetFields,
        pin,
        unpin,
        ...configs
    } = usePinnedFacets(
        config,
        CLIP_FACET_CONFIG.map((c) => c.key),
    );

    const result = useClips({ pageSize: PAGE_SIZES[0] }, { facetFields });
    const baselineResult = useClips(
        { pageSize: 0 },
        { facetFields },
        { paramsOverride: { facets: {}, ranges: {} } },
    );
    const sticky = useStickyFacets(
        result.facet || [],
        baselineResult.facet || [],
    );

    const contextValue: FacetingContextValue<Clip> = useMemo(() => {
        const handleUnpin = (key: string) => {
            result.filterState.clearField(key);
            unpin(key);
        };

        const handlePin = (search: SearchResult) => {
            const keyToPin =
                search.type === 'field' ? search.key : search.facetKey!;
            pin(keyToPin);

            if (search.type === 'value' && search.facetKey) {
                result.filterState.toggleFacetFilter(search.facetKey, {
                    value: search.key,
                    operator: 'exact',
                });
            }
        };

        return {
            ...configs,
            facetData: result.facet || [],
            filterState: result.filterState,
            ...sticky,
            totalResults: baselineResult.itemCount,
            realTimeMatches: result.itemCount,
            getSectionLabel: (key: string) =>
                t(`metadata.${key}`, { ns: 'clipDetails' }),
            getValueLabel: (key: string, val: string) =>
                t(`filters.values.${key}.${val}`, { defaultValue: val }),
            getNormalizer: (key: string) =>
                NORMALIZATION_STRATEGIES[key] ?? ((v: string) => v),
            onPin: handlePin,
            onUnpin: handleUnpin,
        };
    }, [
        configs,
        result.facet,
        result.filterState,
        result.itemCount,
        sticky,
        baselineResult.itemCount,
        unpin,
        pin,
        t,
    ]);

    const columns = useClipColumns();

    return (
        <TableCard<Clip>
            size={'1'}
            {...result}
            tabOptions={tabOptions}
            config={{
                search: {
                    enabled: true,
                    placeholder: t('search.placeholder', { ns: 'clipTable' }),
                },
                selection: {
                    enabled: true,
                    displayKey: 'clipName',
                    summaryKey: 'preservationCategory',
                },
                sorting: { enabled: true },
                columnControl: { enabled: true },
                pagination: { enabled: true, pageSizes: PAGE_SIZES },
                facets: { enabled: true },
                columns,
            }}
            renderFacets={() => (
                <FacetingProvider value={contextValue}>
                    <FacetingPanel.Root>
                        <FacetingPanel.Header
                            title={t('filters.title', { ns: 'clipTable' })}
                        />
                        <FacetingPanel.Content />
                        {/* <FacetingPanel.Footer /> */}
                    </FacetingPanel.Root>
                </FacetingProvider>
            )}
        />
    );
};
